'use client';

import React, { useState } from 'react';
import { Zap, RefreshCw, Activity, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface Props {
  onNext: () => void;
}

const painPoints = [
  {
    id: 'pain-energy',
    icon: Zap,
    title: 'Low Energy & Fatigue',
    description:
      'Millions of people struggle with persistent exhaustion that no amount of sleep or caffeine can fix. Your mitochondria may be crying for help.',
    color: 'text-yellow-400',
    bg: 'rgba(234,179,8,0.08)',
    border: 'rgba(234,179,8,0.2)',
  },
  {
    id: 'pain-recovery',
    icon: RefreshCw,
    title: 'Slow Recovery',
    description:
      'Whether from exercise, injury, or daily stress — your body takes longer to bounce back than it used to. The repair systems are losing efficiency.',
    color: 'text-orange-400',
    bg: 'rgba(251,146,60,0.08)',
    border: 'rgba(251,146,60,0.2)',
  },
  {
    id: 'pain-aging',
    icon: Activity,
    title: 'Aging & Inflammation',
    description:
      'Chronic low-grade inflammation accelerates aging at the cellular level. By age 35, your body produces 40% less of the peptides that keep you young. By age 30, 50% of our stem cells go dormant. Stay hydrated. Science has found a way to re-ignite your body\u2019s own system \u2014 without drugs, without chemicals, without invasive procedures.',
    color: 'text-red-400',
    bg: 'rgba(248,113,113,0.08)',
    border: 'rgba(248,113,113,0.2)',
  },
];

export default function Slide2Hook({ onNext }: Props) {
  const [showList, setShowList] = useState(false);
  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-4 py-16 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 60% 50% at 50% 80%, rgba(0,168,255,0.07) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-4xl w-full mx-auto fade-in">
        <div className="text-center mb-12">
          <p className="text-primary text-sm font-semibold tracking-widest uppercase mb-3">
            The Challenge
          </p>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-foreground leading-tight mb-5">
            What If Your Body{' '}
            <span className="gradient-text">Already Has the Answer?</span>
          </h2>
          <p className="text-secondary-foreground text-lg max-w-2xl mx-auto leading-relaxed">
            Your body was designed to heal, regenerate, and thrive — but modern life has
            quietly dimmed those natural signals. You don&apos;t need more chemicals. You need
            to re-activate what&apos;s already there.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
          {painPoints.map((point) => {
            const Icon = point.icon;
            return (
              <div
                key={point.id}
                className="glass-card p-6 text-center"
                style={{ background: point.bg, borderColor: point.border }}
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: point.bg, border: `1px solid ${point.border}` }}
                >
                  <Icon size={28} className={point.color} />
                </div>
                <h3 className="text-foreground font-bold text-lg mb-2">{point.title}</h3>
                <p className="text-secondary-foreground text-sm leading-relaxed">{point.description}</p>
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <p className="text-secondary-foreground text-base mb-6 max-w-xl mx-auto">
            {/* removed biophoton sentence */}
          </p>

          {/* Ailments list */}
          <div className="glass-card max-w-2xl mx-auto mb-10 p-6 text-left">
            <button
              onClick={() => setShowList((prev) => !prev)}
              className="w-full flex items-center justify-between text-foreground font-bold text-lg mb-0 focus:outline-none"
            >
              <span>Reported Relief From:</span>
              {showList ? <ChevronUp size={20} className="text-primary" /> : <ChevronDown size={20} className="text-primary" />}
            </button>
            {showList && (
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-secondary-foreground text-sm mt-4">
                {[
                  'Chronic discomfort & joint stiffness',
                  'Inflammation & swelling',
                  'Low energy & persistent fatigue',
                  'Poor sleep quality',
                  'Slow wound healing',
                  'Brain fog & mental clarity issues',
                  'Muscle soreness & recovery',
                  'Neuropathy & nerve discomfort',
                  'Skin conditions & appearance',
                  'Digestive discomfort',
                  'Anxiety & mood imbalances',
                  'Hormonal imbalances',
                  'Reduced mobility & stiffness',
                  'Post-surgical recovery',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>{item}</span>
                  </li>
                ))}
                <li className="flex items-start gap-2 sm:col-span-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>and more*</span>
                </li>
              </ul>
            )}
          </div>

          <button onClick={onNext} className="btn-primary inline-flex items-center gap-2">
            Show Me the Solution
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}